import { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getAdyenPaymentProfits } from 'apis/reportAPIs';
import ReportDateRange from 'components/ReportDateRange/ReportDateRange';
import { showErrorSnackbar } from 'store/actions/snackbarMessage';

// Admin only (the server enforces it): what OrderBuddy made on each Adyen payment, worked out from
// Adyen's transfer webhooks as commission less Adyen's payment fee and the fixed 7c transaction fee,
// less what refunds of the payment cost OrderBuddy (the commission handed back and any refund fee on OrderBuddy's accounts).

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  clickable: {
    cursor: 'pointer',
  },
}));

// Colours go on sx: MUI's TableCell styles override makeStyles classes.

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const today = () => moment().format('YYYY-MM-DD');
// What Adyen charged as a share of the merchant amount: (payment fee + transaction fee) / merchant amount.
// Null when there is no merchant amount to divide by.
const costRate = (row) => {
  const merchantAmount = Number(row.merchantAmount || 0);
  if (!merchantAmount) return null;
  return (Number(row.paymentFee || 0) + Number(row.transactionFee || 0)) / merchantAmount * 100;
};
const percent = (value) => (value === null ? '-' : `${value.toFixed(2)}%`);
// The merchant's current domestic commission setting; the totals row has none.
const domesticRate = (row) => {
  if (!('domesticRate' in row)) return '-';
  return row.domesticRate === null ? 'Not set' : percent(Number(row.domesticRate));
};
const isBelowCost = (row) =>
  row.domesticRate !== null && row.domesticRate !== undefined && costRate(row) !== null
  && Number(row.domesticRate) < costRate(row);

// Green when OrderBuddy made money, red when it lost money.
const ProfitCell = ({ profit }) => {
  let color;
  if (profit > 0) color = 'success.dark';
  if (profit < 0) color = 'error.main';
  return (
    <TableCell align="right" sx={{ color }}><strong>{money(profit)}</strong></TableCell>
  );
};

ProfitCell.propTypes = {
  profit: PropTypes.number,
};

const AmountCells = ({ row }) => {
  const belowCost = isBelowCost(row);
  return (
    <>
      <TableCell align="right">{row.count}</TableCell>
      <TableCell align="right">{money(row.merchantAmount)}</TableCell>
      <TableCell align="right">{money(row.commission)}</TableCell>
      <TableCell align="right">{money(row.paymentFee)}</TableCell>
      <TableCell align="right">{money(row.transactionFee)}</TableCell>
      <TableCell align="right">{percent(costRate(row))}</TableCell>
      <TableCell
        align="right"
        sx={belowCost ? { color: 'warning.dark', fontWeight: 'bold' } : undefined}
        title={belowCost ? 'Below the cost rate' : undefined}
      >
        {domesticRate(row)}
      </TableCell>
      <TableCell align="right">{money(row.refunded)}</TableCell>
      <TableCell align="right">{money(row.refundCost)}</TableCell>
      <ProfitCell profit={row.profit} />
    </>
  );
};

AmountCells.propTypes = {
  row: PropTypes.shape({
    count: PropTypes.number,
    merchantAmount: PropTypes.number,
    commission: PropTypes.number,
    paymentFee: PropTypes.number,
    transactionFee: PropTypes.number,
    domesticRate: PropTypes.number,
    refunded: PropTypes.number,
    refundCost: PropTypes.number,
    profit: PropTypes.number,
  }).isRequired,
};

const AmountHeadings = () => (
  <>
    <TableCell align="right">Payments</TableCell>
    <TableCell align="right">Merchant Amount</TableCell>
    <TableCell align="right">Commission</TableCell>
    <TableCell align="right">Payment Fee</TableCell>
    <TableCell align="right">Transaction Fee</TableCell>
    <TableCell align="right">Cost Rate</TableCell>
    <TableCell align="right">Domestic Rate</TableCell>
    <TableCell align="right">Refunded</TableCell>
    <TableCell align="right">Refund Cost</TableCell>
    <TableCell align="right">Profit</TableCell>
  </>
);

const AdyenPaymentProfits = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [startDateStr, setStartDateStr] = useState(today());
  const [endDateStr, setEndDateStr] = useState(today());
  const [report, setReport] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = async (restaurantId = null) => {
    setIsLoading(true);
    try {
      const data = await getAdyenPaymentProfits(startDateStr, endDateStr, restaurantId);
      setReport(data);
      setMerchant(restaurantId);
    } catch (error) {
      console.error('getAdyenPaymentProfits', error);
      if (error.response && error.response.status === 403) {
        history.push('/sign-in');
        return;
      }
      const message = error.response && error.response.data && error.response.data.message;
      dispatch(showErrorSnackbar(message || 'Cannot load Adyen payment profits'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>Adyen Payment Profits</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Profit = commission − Adyen payment fee − transaction fee ($0.07 per payment) − refund cost
        (commission handed back and refund fees OrderBuddy paid), from Adyen transfer webhooks. Cost rate = (payment fee +
        transaction fee) / merchant amount; domestic rate is the merchant&apos;s current domestic commission setting. Refunds count on their payment&apos;s date. Sydney
        time, up to 31 days.
      </Typography>

      <div className={classes.filters}>
        <ReportDateRange
          startDateStr={startDateStr}
          endDateStr={endDateStr}
          onStartChange={setStartDateStr}
          onEndChange={setEndDateStr}
        />
        <Button variant="contained" color="primary" disabled={isLoading} onClick={() => load()}>
          Search
        </Button>
      </div>
      {isLoading && <LinearProgress />}

      {report && (
        <>
          <TableContainer component={Paper} className={classes.section}>
            <Table size="small" aria-label="Adyen payment profit by merchant">
              <TableHead>
                <TableRow>
                  <TableCell>Merchant</TableCell>
                  <AmountHeadings />
                </TableRow>
              </TableHead>
              <TableBody>
                {report.merchants.map((row) => (
                  <TableRow
                    key={row.restaurantId}
                    hover
                    selected={row.restaurantId === merchant}
                    className={classes.clickable}
                    onClick={() => load(row.restaurantId)}
                  >
                    <TableCell>{row.restaurantName || row.restaurantId}</TableCell>
                    <AmountCells row={row} />
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell><strong>Total</strong></TableCell>
                  <AmountCells row={report.totals} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {merchant && report.payments && (
            <TableContainer component={Paper} className={classes.section}>
              <Typography variant="h6" style={{ padding: 16 }}>
                Payments: {merchant}
              </Typography>
              <Table size="small" aria-label="Adyen payment profit by payment">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>PSP Reference</TableCell>
                    <TableCell>Merchant Reference</TableCell>
                    <TableCell align="right">Merchant Amount</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Payment Fee</TableCell>
                    <TableCell align="right">Transaction Fee</TableCell>
                    <TableCell align="right">Cost Rate</TableCell>
                    <TableCell align="right">Refunded</TableCell>
                    <TableCell align="right">Refund Cost</TableCell>
                    <TableCell align="right">Profit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.payments.map((row) => (
                    <TableRow key={row.pspReference}>
                      <TableCell>{row.createdAt ? moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss') : '-'}</TableCell>
                      <TableCell>{row.pspReference}</TableCell>
                      <TableCell>{row.merchantReference}</TableCell>
                      <TableCell align="right">{money(row.merchantAmount)}</TableCell>
                      <TableCell align="right">{money(row.commission)}</TableCell>
                      <TableCell align="right">{money(row.paymentFee)}</TableCell>
                      <TableCell align="right">{money(row.transactionFee)}</TableCell>
                      <TableCell align="right">{percent(costRate(row))}</TableCell>
                      <TableCell align="right">{money(row.refunded)}</TableCell>
                      <TableCell align="right">{money(row.refundCost)}</TableCell>
                      <ProfitCell profit={row.profit} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </div>
  );
};

export default AdyenPaymentProfits;
